import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { MediaSlotView, NARROW_SCREEN_ONLY, WIDE_SCREEN_ONLY, } from "../render/context";
import { resolveSlotMedia } from "../render/slot-media";
import { Section } from "./_section";
/**
 * MediaBlock — a full-width picture band holding one picture per screen.
 *
 * Each screen's picture is its own stored reference to a library photo cropped
 * at that screen's size (see MEDIA_SCREEN_IMAGE_SLOTS in @diality/contracts), so
 * the same photo cropped two ways and two entirely different photos render the
 * same way here. This is the renderer for BOTH the production `Media` block and
 * its test-only twin `PlaygroundMedia` — one component, so the fixture the
 * acceptance examples drive is the code a visitor gets.
 *
 * CRITICAL LAYOUT CONSTRAINT: which picture a screen receives is a container
 * query against the `@container w-full` wrapper BlockRenderer adds
 * (block-renderer.tsx:53), so the block reacts to its OWN width. Nothing in this
 * markup may cap the width of the pictures' container below full width, or the
 * screens will never switch. Keep the markup minimal; no interactive elements.
 *
 * AND THE CHOICE IS CSS, NEVER JS. Both pictures are rendered and the container
 * step displays exactly one; the un-served screen's picture is `display:none`,
 * so it is loaded but laid out nowhere. That is what lets a visitor who widens
 * their window or turns their phone see the change immediately, with no network
 * and no re-render — and it is why nothing here may read `innerWidth`. A framing
 * decided in a `useEffect` lands after hydration, which a real visitor
 * experiences as a flash and the acceptance Thens cannot see at all
 * (docs/briefs/playground-components.md §5, probe 5).
 */
const CONTAINER = "w-full px-4 @min-[640px]:px-6 @min-[1024px]:px-8";
export function MediaBlock({ data }) {
    const { caption, desktop, mobile } = data;
    return (_jsx(Section, { children: _jsx("div", { className: CONTAINER, children: _jsxs("figure", { className: "w-full", children: [_jsx(MediaSlotView, { image: mobile, ratio: "9 / 16", label: "Mobile picture", className: NARROW_SCREEN_ONLY, emptyRender: null }), _jsx(MediaSlotView, { image: desktop, ratio: "16 / 9", label: "Desktop picture", className: WIDE_SCREEN_ONLY, emptyRender: null }), caption && (_jsx("figcaption", { className: "pt-3 text-sm text-muted-foreground", children: caption }))] }) }) }));
}
export function mediaFromBlock(block, resolve) {
    const s = block.scalars ?? {};
    return {
        caption: s.caption || undefined,
        // Each screen resolved from its OWN slot. Both slots are single-target, so
        // each of these is one framing and resolveSlotMedia's multi-target `sources`
        // branch is never taken — deriving one screen's picture from the other's ref
        // is exactly what this block must not do.
        desktop: resolveSlotMedia(block, "desktopImage", resolve),
        mobile: resolveSlotMedia(block, "mobileImage", resolve),
    };
}
export const mediaBlockEntry = {
    Component: MediaBlock,
    fromBlock: mediaFromBlock,
};
//# sourceMappingURL=media.js.map